import React, { useState, useRef } from 'react';
import { ReactComponent as DragIcon } from 'icons/icon-drag.svg';

import {
  TableRow,
  EntityCell,
  CellContent,
  StyledEntityItem,
} from './TableRow.style';
import Button from 'components/elements/button';
import { IBrowseCustomListEntityData } from 'pages/CustomList/shared';
import styled from 'styled-components/macro';
import { ThemeProps } from 'types/styled';
import useReorderDragDrop from 'hooks/useReorderDragDrop';

const DragHandler = styled.div<ThemeProps<'div'>>`
  padding: ${({ theme }): string => theme.spacing.xsmall};
  cursor: move;
  line-height: 1;
`;

const EntityCellStyled = styled(EntityCell)`
  cursor: ${({ isLast }): string => (isLast ? 'not-allowed' : 'auto')};
`;

interface IEditorTableRowProps {
  entity: any;
  entityData: IBrowseCustomListEntityData;
  onDelete: () => void;
  onUndelete: () => void;
  disableDelete: boolean;
  changeEntityOrder: (oldIndex: number, newIndex: number) => void;
  index: number;
}

/**
 * EditorTableRow is a single table row displaying an entity.
 * The row has two states:
 * - A normal state that shows a delete button, the entity info and a drag button to re-order the row.
 * - A deleted state that shows the entity info and an undelete button.
 */
const EditorTableRow: React.FC<IEditorTableRowProps> = (props) => {
  const {
    entity,
    entityData,
    onDelete,
    onUndelete,
    disableDelete,
    changeEntityOrder,
    index,
  } = props;
  const [isDeleted, setIsDeleted] = useState(false);
  const ref = useRef(null);

  const { isDragging } = useReorderDragDrop(
    ref,
    entity,
    index,
    changeEntityOrder
  );
  const opacity = isDragging ? 0 : 1;

  const doDelete = (): void => {
    if (!disableDelete) {
      setIsDeleted(true);
      onDelete();
    }
  };

  const undoDelete = (): void => {
    setIsDeleted(false);
    onUndelete();
  };

  return (
    <TableRow isDeleted={isDeleted} isEditMode ref={ref} style={{ opacity }}>
      <EntityCellStyled isLast={disableDelete}>
        {!isDeleted && (
          <Button
            onClick={doDelete}
            variant={disableDelete ? 'disabled' : ''}
            bg={'VeryLightGrey'}
            height="2em"
            width="2em"
            display="flex"
            alignItems="center"
            justifyContent="center"
            lineHeight={1}
            data-pw="custom-list-update-list-delete-button"
          >
            x
          </Button>
        )}
      </EntityCellStyled>
      <EntityCell>
        <CellContent>
          <StyledEntityItem
            entityId={entity.id}
            entityType={entity.type}
            displayName={entity.displayName}
            primaryArtist={entity.primaryArtist}
            thumbnailUrl={entity.thumbnailUrl}
            releaseDate={entity.releaseDate}
            isGroup={entity.is_group}
            parentEntityId={entity.parent_group_id}
            groupChildCount={entity.group_child_count}
            entityData={entityData}
            releaseCount={entity.grouped_album_count}
            artistCount={entity.artist_count}
            trackCount={entity.grouped_track_count}
            preserveSearch={true}
          />
        </CellContent>
      </EntityCell>
      <EntityCell className="buttonColumn">
        {isDeleted && (
          <Button variant="secondary" onClick={undoDelete} py={'.75rem'}>
            Undo delete
          </Button>
        )}
        {!isDeleted && (
          <DragHandler onMouseDown={(e: any): any => e.stopPropagation()}>
            <DragIcon />
          </DragHandler>
        )}
      </EntityCell>
    </TableRow>
  );
};

export default EditorTableRow;
